import React, { useMemo, useState, useCallback, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { INVENTORY } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import GridToolbar from '../components/GridToolbar';
import '../styles/DataGrid.css';

const InventoryPage = () => {
  const gridRef = useRef();
  const { t, fmtCurrency, lang } = useLang();
  const [rowData, setRowData] = useState(INVENTORY.map((item, i) => ({ ...item, _id: i })));

  const statusLabel = (s) => {
    if (s === '정상') return t.inventory.normal;
    if (s === '주의') return t.inventory.caution;
    return t.inventory.needOrder;
  };

  const groupLabel = (g) => {
    if (lang === 'ja') {
      if (g === '메모리') return 'メモリ';
      if (g === '비메모리') return '非メモリ';
      if (g === 'SiP 외') return 'SiP他';
    }
    return g;
  };

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 48, pinned: 'left', lockPosition: true, suppressMenu: true, resizable: false },
    { field: 'code', headerName: t.inventory.code, width: 120, pinned: 'left', editable: false },
    { field: 'name', headerName: t.inventory.name, width: 220, editable: false },
    { field: 'group', headerName: t.inventory.group, width: 100,
      valueFormatter: params => groupLabel(params.value),
      cellStyle: params => {
        const colors = { '메모리': '#dbeafe', '비메모리': '#f3e8ff', 'SiP 외': '#ccfbf1' };
        return { backgroundColor: colors[params.value] || 'transparent', fontWeight: 500 };
      }
    },
    { field: 'good', headerName: 'Good', width: 100, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0 },
    { field: 'reserved', headerName: 'Reserved', width: 110, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0 },
    { field: 'defect', headerName: 'Defect', width: 100, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0,
      cellStyle: params => params.value > 0 ? { color: '#dc2626', fontWeight: 700 } : {} },
    { field: 'atp', headerName: 'ATP', width: 110, editable: false, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(),
      cellStyle: params => ({ fontWeight: 700, color: params.value <= 0 ? '#dc2626' : params.value < 100 ? '#f59e0b' : '#16a34a' }) },
    { field: 'status', headerName: t.inventory.status, width: 100,
      valueFormatter: params => statusLabel(params.value),
      cellStyle: params => {
        if (params.value === '발주필요') return { backgroundColor: '#fef2f2', color: '#dc2626', fontWeight: 600 };
        if (params.value === '주의') return { backgroundColor: '#fefce8', color: '#a16207', fontWeight: 600 };
        return { backgroundColor: '#f0fdf4', color: '#16a34a', fontWeight: 600 };
      }
    },
    { field: 'cost', headerName: t.inventory.cost, width: 110, type: 'numericColumn',
      valueFormatter: params => fmtCurrency(params.value) },
    { field: 'lossAmt', headerName: t.inventory.lossAmt, width: 130, type: 'numericColumn',
      valueFormatter: params => params.value > 0 ? fmtCurrency(params.value) : '-',
      cellStyle: params => params.value > 0 ? { color: '#dc2626', fontWeight: 600 } : {} },
  ], [t, lang, fmtCurrency]);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const onCellValueChanged = useCallback((event) => {
    const row = event.data;
    row.atp = row.good - row.reserved;
    row.needOrder = row.atp <= 0;
    row.status = row.atp <= 0 ? '발주필요' : row.atp < 100 ? '주의' : '정상';
    row.lossAmt = row.defect * row.cost;
    event.api.refreshCells({ rowNodes: [event.node] });
  }, []);

  const newRowTemplate = useCallback((data) => ({
    _id: Date.now(), code: '', name: '(신규)', group: '', cls: '', cost: 0,
    good: 0, defect: 0, reserved: 0, atp: 0, needOrder: false, lossAmt: 0, status: '정상',
  }), []);

  const needOrder = useMemo(() => rowData.filter(r => r.needOrder).length, [rowData]);
  const totalDefect = useMemo(() => rowData.reduce((s, r) => s + r.defect, 0), [rowData]);
  const totalLoss = useMemo(() => rowData.reduce((s, r) => s + r.lossAmt, 0), [rowData]);

  return (
    <div className="data-page">
      <div className="data-page-header">
        <div className="data-page-title-area">
          <h2 className="data-page-title">{t.inventory.title}</h2>
          <span className="data-page-count">{rowData.length}{lang === 'ko' ? '개 품목' : '品目'}</span>
        </div>
        <div className="data-page-badges">
          <span className="badge badge-red">{t.inventory.needOrder}: {needOrder}{t.dashboard.count}</span>
          <span className="badge badge-orange">{lang === 'ko' ? '총 불량' : '総不良'}: {totalDefect.toLocaleString()} EA</span>
          <span className="badge badge-red">{t.inventory.lossAmt}: {fmtCurrency(totalLoss)}</span>
        </div>
      </div>
      <GridToolbar gridRef={gridRef} rowData={rowData} setRowData={setRowData} newRowTemplate={newRowTemplate} entityName={lang === 'ko' ? '재고' : '在庫'} />
      <div className="ag-theme-alpine data-grid-wrapper">
        <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
          onCellValueChanged={onCellValueChanged} animateRows={true} rowSelection="multiple"
          enableCellTextSelection={true} undoRedoCellEditing={true} undoRedoCellEditingLimit={20}
          stopEditingWhenCellsLoseFocus={true} suppressRowClickSelection={true}
        />
      </div>
    </div>
  );
};

export default InventoryPage;
